/**
 * Project Service with Intelligent Caching
 * Provides fast data access with background refresh capabilities
 */

import cacheService from './cacheService';
import { API_BASE_URL } from '../API/api';

class ProjectService {
  constructor() {
    console.log('🚀 ProjectService initialized with intelligent caching');
    
    // Add debugging to intercept all fetch calls
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      const url = args[0];
      if (typeof url === 'string' && url.includes('null')) {
        console.trace('🔍 PROJECTSERVICE NULL FETCH CALL DETECTED:', url);
        console.trace('🔍 Stack trace:', new Error().stack);
      }
      return originalFetch(...args);
    };
  }

  /**
   * Fetch all projects from the database with intelligent caching
   * @param {boolean} forceRefresh - Force refresh from API
   * @param {string} userEmail - User email for filtering projects
   * @returns {Promise<Array>} Array of project objects
   */
  async getAllProjects(forceRefresh = false, userEmail = null) {
    console.log('🚀 getAllProjects called with caching', { forceRefresh, userEmail });
    
    try {
      // Clear cache if forcing refresh to ensure we get fresh filtered data
      if (forceRefresh) {
        console.log('🧹 Clearing projects cache for fresh fetch...');
        cacheService.clear('projects', {});
      }
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedData = cacheService.get('projects');
        if (cachedData) {
          console.log('✅ Projects loaded from cache:', cachedData.length, 'items');
          return cachedData;
        }
      }
      
      // Fetch from API - use user-filtered endpoint if email is provided
      let apiUrl = `${API_BASE_URL}/api/projects`;
      if (userEmail) {
        apiUrl = `${API_BASE_URL}/api/user-projects?email=${encodeURIComponent(userEmail)}`;
        console.log('🌐 Using user-filtered projects endpoint for:', userEmail);
        console.log('🔍 DEBUG: Full API URL:', apiUrl);
      } else {
        console.log('🌐 Using all projects endpoint (no user email provided)');
        console.log('🚨 WARNING: No user email provided - will show ALL projects!');
      }
      
      console.log('🌐 Fetching projects from API...', apiUrl);
      const response = await fetch(apiUrl);
      console.log('🌐 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🌐 API Response data:', data);
      console.log('🔍 DEBUG: Number of projects returned:', data.projects ? data.projects.length : 'No projects array');
      console.log('🔍 DEBUG: Response structure:', {
        hasProjects: !!data.projects,
        projectsLength: data.projects ? data.projects.length : 0,
        message: data.message || 'No message',
        success: data.success !== undefined ? data.success : 'No success field'
      });
      
      if (data.projects) {
        const formattedProjects = data.projects.map(project => this.formatProjectForFrontend(project));
        console.log('✅ Formatted projects:', formattedProjects.length, 'items');
        
        // Cache the results
        cacheService.set('projects', {}, formattedProjects);
        console.log('💾 Projects cached successfully');
        
        return formattedProjects;
      } else {
        console.warn('⚠️ No projects in response data');
        return [];
      }
    } catch (error) {
      console.error('❌ Error in getAllProjects:', error);
      throw error;
    }
  }

  /**
   * Fetch a specific project by its code - DIRECT API CALL
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Project object
   */
  async getProjectByCode(projectCode, forceRefresh = false) {
    console.log('🔧 getProjectByCode called for:', projectCode);
    console.log('🔧 getProjectByCode - Individual project routes removed, returning null');
    
    // Individual project routes have been removed - return null
    return null;
  }

  /**
   * Fetch project-specific data - DIRECT API CALL
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Project data object
   */
  async getProjectData(projectCode, forceRefresh = false) {
    console.log('🔧 getProjectData called for:', projectCode);
    console.log('🔧 getProjectData - Individual project routes removed, returning empty object');
    
    // Individual project routes have been removed - return empty object
    return {};
  }

  /**
   * Search projects by name with intelligent caching
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching projects
   */
  async searchProjects(query) {
    console.log('🔍 searchProjects called with query:', query);
    
    try {
      // Check cache first
      const cachedData = cacheService.get('projectsSearch', { query });
      if (cachedData) {
        console.log('✅ Search results loaded from cache');
        return cachedData;
      }
      
      // Fetch from API
      console.log('🌐 Fetching search results from API...');
      const response = await fetch(`${API_BASE_URL}/api/projectsSearch?query=${encodeURIComponent(query)}`);
      console.log('🌐 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🌐 API Response data:', data);
      
      const results = data || [];
      
      // Cache the results
      cacheService.set('projectsSearch', { query }, results);
      console.log('💾 Search results cached successfully');
      
      return results;
    } catch (error) {
      console.error('❌ Error in searchProjects:', error);
      throw error;
    }
  }

  /**
   * Convert project data from database format to frontend format
   * @param {Object} dbProject - Project data from database
   * @returns {Object} Formatted project object for frontend
   */
  formatProjectForFrontend(dbProject) {
    console.log('🔧 formatProjectForFrontend called with:', dbProject);
    
    // Handle both database format (id, name, expiration) and API format (Id, Name, Expiration)
    const id = dbProject.Id || dbProject.id;
    const name = dbProject.Name || dbProject.name;
    const expiration = dbProject.Expiration || dbProject.expiration;
    const created = dbProject.Modified || dbProject.created;
    const lastviewed = dbProject.LastViewed || dbProject.lastviewed;
    
    const formatted = {
      Id: id,
      Name: name,
      Code: String(id), // Use ID directly without padding
      Plan: dbProject.Plan || 'Standard', // Default value
      UserRole: dbProject.UserRole || 'Administrator', // Default value
      Expiration: expiration ? new Date(expiration) : null,
      ThumbnailURL: dbProject.ThumbnailURL || null, // Will show fallback
      UserRoleId: dbProject.UserRoleId || 1, // Default value
      Modified: created ? new Date(created) : new Date(),
      ModelModified: created ? new Date(created) : new Date(),
      LastViewed: lastviewed ? new Date(lastviewed) : null,
      Viewable: dbProject.Viewable !== undefined ? dbProject.Viewable : true,
      Size: dbProject.Size || 0, // Default value
      Members: dbProject.Members || 1, // Default value
      Apps: dbProject.Apps || 0, // Default value
      AccountManagerEmail: dbProject.AccountManagerEmail || 'manager@rdvsystems.com', // Default value
      ProjectType: dbProject.ProjectType || 'visualization' // visualization | rsa-model | rsa-existing from 123bimlive_Project_type
    };
    
    console.log('🔧 Formatted project:', formatted);
    return formatted;
  }

  // ===== SIMPLIFIED UTILITY METHODS =====
  
  /**
   * Check if a project is expired
   */
  isProjectExpired(project) {
    if (!project.Expiration) return false;
    return new Date(project.Expiration) <= new Date();
  }

  /**
   * Check if a project can be viewed
   */
  canViewProject(project) {
    return project.Viewable && !this.isProjectExpired(project);
  }

  /**
   * Check if a project can be accessed for files
   */
  canAccessProjectFiles(project) {
    return project.UserRoleId < 4 && !this.isProjectExpired(project);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format date for display
   */
  formatDate(date) {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + 
           dateObj.getHours().toString().padStart(2, '0') + ':' + 
           dateObj.getMinutes().toString().padStart(2, '0');
  }

  // ===== INTELLIGENT CACHE METHODS =====
  
  clearCache() {
    console.log('🧹 Clearing all cache...');
    cacheService.clearAll();
  }

  clearProjectCache(projectCode) {
    console.log('🧹 Clearing project cache for:', projectCode);
    cacheService.clear('projects', {});
    cacheService.clear('projectsSearch', {});
  }

  async preloadAllProjects() {
    console.log('⚡ Preloading all projects...');
    return await this.getAllProjects(false);
  }

  async preloadProject(projectCode) {
    console.log('⚡ Preloading project:', projectCode);
    // Since individual project routes are removed, return null
    return null;
  }

  getCacheStats() {
    const stats = cacheService.getStats();
    console.log('📊 Cache stats:', stats);
    return stats || { hits: 0, misses: 0, size: 0 };
  }

  isProjectsCached() {
    const cached = cacheService.get('projects');
    return !!cached;
  }

  isProjectCached(projectCode) {
    // Since individual project routes are removed, always return false
    return false;
  }

  handleDataChange(action, projectCode = null) {
    console.log('🔄 Handling data change:', action, projectCode);
    
    // Clear relevant cache entries when data changes
    switch (action) {
      case 'project_created':
      case 'project_updated':
      case 'project_deleted':
        this.clearProjectCache(projectCode);
        break;
      case 'projects_refresh':
        this.clearProjectCache(null);
        break;
      default:
        console.log('Unknown data change action:', action);
    }
  }

  isDataFresh(projectCode = null) {
    if (projectCode) {
      return this.isProjectCached(projectCode);
    }
    return this.isProjectsCached();
  }

  getDataAge(projectCode = null) {
    // This would require more complex cache metadata tracking
    // For now, return a simple boolean
    return this.isDataFresh(projectCode) ? 0 : Infinity;
  }

}

// Create and export a singleton instance
const projectService = new ProjectService();
export default projectService;
